/*!
 * Oneskin Stat Group — vanilla JS controller for the Stat Group section
 * (intersection-triggered count-up + optional reveal animation).
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'stat-group.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces:
 *   frontend/scripts/sections/stat-group.js
 *   frontend/scripts/lib/animation/stat-group.js
 *   Snap.svg CDN dependency
 */
;(() => {
  'use strict'

  if (window.__oneskinStatGroup) return
  window.__oneskinStatGroup = true

  const SECTION_TYPE = 'stat-group'
  const MOBILE_BREAKPOINT = 768

  function prefersReducedMotion() {
    return document.documentElement.classList.contains('prefers-reduced-motion')
  }

  function shouldAnimate(node) {
    return node.classList.contains('animation') && !prefersReducedMotion()
  }

  function delayOffset(node, selectorList, offsetStart = 0) {
    let offset = offsetStart
    selectorList.forEach((selector) => {
      node.querySelectorAll(selector).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', String(offset))
        offset++
      })
    })
  }

  function intersectionWatcher(node) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(node)

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  function countDecimalPlaces(num) {
    if (!Number.isFinite(num)) return 0
    const parts = String(num).split('.')
    return parts.length > 1 ? parts[1].length : 0
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
  }

  function formatValue(value, decimals, useLocale) {
    if (decimals > 0) {
      const fixed = value.toFixed(decimals)
      return useLocale ? Number(fixed).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }) : fixed
    }

    const rounded = Math.round(value)
    return useLocale ? rounded.toLocaleString() : String(rounded)
  }

  function animateCount(el, target, duration, useLocale, onDone) {
    const decimals = countDecimalPlaces(target)
    const unit = el.dataset.unit || ''

    if (prefersReducedMotion() || duration <= 0) {
      el.textContent = formatValue(target, decimals, useLocale) + unit
      onDone?.()
      return () => {}
    }

    let frameId = 0
    const start = performance.now()

    function tick(now) {
      const progress = Math.min((now - start) / duration, 1)
      const current = target * easeInOut(progress)
      el.textContent = formatValue(current, decimals, useLocale) + unit

      if (progress < 1) {
        frameId = requestAnimationFrame(tick)
      } else {
        el.textContent = formatValue(target, decimals, useLocale) + unit
        onDone?.()
      }
    }

    frameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }

  function initStatGroup(container) {
    const duration = parseFloat(container.dataset.duration || '1500') || 1500
    const useLocale = container.dataset.pdpFormat !== 'true'
    const numberEls = Array.from(container.querySelectorAll('[data-stat-number]'))
    const cancelers = []
    let hasAnimated = false
    let isMobile = window.innerWidth < MOBILE_BREAKPOINT
    let lastWidth = window.innerWidth
    let resizeTimeout = 0
    let revealAnimation = null

    function setInitialValues() {
      numberEls.forEach((el) => {
        const target = parseFloat(el.dataset.number || '0')
        const decimals = countDecimalPlaces(target)
        const unit = el.dataset.unit || ''
        el.textContent = formatValue(0, decimals, useLocale) + unit
      })
    }

    function runCountUp() {
      cancelers.splice(0).forEach((cancel) => cancel())
      numberEls.forEach((el) => {
        const target = parseFloat(el.dataset.number || '0')
        cancelers.push(animateCount(el, target, duration, useLocale))
      })
    }

    function onIntersect(entries, observer) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.7) return

        if (isMobile) {
          if (!hasAnimated) {
            runCountUp()
            hasAnimated = true
          }
        } else {
          runCountUp()
        }

        observer.unobserve(entry.target)
      })
    }

    const countObserver = new IntersectionObserver(onIntersect, { threshold: 0.7 })

    setInitialValues()
    countObserver.observe(container)

    function onResize() {
      clearTimeout(resizeTimeout)
      resizeTimeout = window.setTimeout(() => {
        const newWidth = window.innerWidth
        const wasMobile = isMobile
        isMobile = newWidth < MOBILE_BREAKPOINT

        if (wasMobile !== isMobile) {
          hasAnimated = false
        }

        if (lastWidth !== newWidth) {
          runCountUp()
          lastWidth = newWidth
        }
      }, 300)
    }

    window.addEventListener('resize', onResize)

    if (shouldAnimate(container)) {
      delayOffset(container, ['.stat-group__inner > *'])
      revealAnimation = intersectionWatcher(container)
    }

    return {
      unload() {
        countObserver.disconnect()
        window.removeEventListener('resize', onResize)
        clearTimeout(resizeTimeout)
        cancelers.splice(0).forEach((cancel) => cancel())
        revealAnimation?.destroy()
      },
    }
  }

  const instances = new WeakMap()

  function initContainer(container) {
    if (!container || instances.has(container)) return
    if (container.dataset.sectionType !== SECTION_TYPE) return
    instances.set(container, initStatGroup(container))
  }

  function unloadContainer(container) {
    instances.get(container)?.unload?.()
    instances.delete(container)
  }

  function autoInit(root = document) {
    root.querySelectorAll(`[data-section-type="${SECTION_TYPE}"]`).forEach(initContainer)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  function containerFor(event) {
    const sectionId = event.detail?.sectionId
    return sectionId ? document.querySelector(`[data-section-id="${sectionId}"]`) : null
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container = containerFor(event)
    if (container) initContainer(container)
    else autoInit(event.target)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = containerFor(event)
    if (container) unloadContainer(container)
  })
})()
